import React from "react";
import WebProfitSpinner from "../../system/WebProfitSpinner";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import RestaurantMenuRoundedIcon from "@mui/icons-material/RestaurantMenuRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { recipesApi } from "../../../api/recipes";
import { getProductImageUrls } from "../../../utils/productMedia";
import DashboardLoadingState from "../DashboardLoadingState";

const DARK = "var(--wp-panel-text-primary, #19242F)";
const ORANGE = "#F35A3A";

function idOf(product) {
  return String(product?.id || product?._id || "");
}

function nameOf(product) {
  return product?.name || product?.nombre || "Sin nombre";
}

function kindOf(product = {}) {
  const value = String(product.erpItemKind || product.itemKind || product.itemClass || product.tipo || "product").toLowerCase();
  if (["supply", "product", "service", "expense", "asset", "charge"].includes(value)) return value;
  return value === "servicio" ? "service" : "product";
}

function stockTracked(product = {}) {
  if (typeof product.operational?.stockTracked === "boolean") return product.operational.stockTracked;
  return ["product", "supply"].includes(kindOf(product));
}

function active(product = {}) {
  return product.active !== false && product.activo !== false;
}

function sellableProduct(product = {}) {
  return kindOf(product) === "product" && product.operational?.sellable !== false && active(product);
}

function unitOf(product = {}) {
  return String(product.unit || product.unidad || "").trim();
}

function recipeSummaryText(productName, rows) {
  if (!rows.length) return "Todavía no hay ingredientes configurados.";
  const parts = rows.map((row) => `${row.quantityPerServing || "?"} ${row.unitSnapshot || row.product?.unit || ""} de ${row.product?.name || row.productNameSnapshot || "ingrediente"}`.trim());
  return `Cada ${productName} descuenta: ${parts.join(", ")}.`;
}

function ingredientDraft(recipe) {
  return (recipe?.ingredients || []).map((row) => ({
    productId: String(row.productId || ""),
    quantityPerServing: String(row.quantityPerServing ?? ""),
    unitSnapshot: row.product?.unit || row.unitSnapshot || "",
    productNameSnapshot: row.product?.name || row.productNameSnapshot || "Ingrediente",
    product: row.product || null,
    issue: row.issue || null,
  }));
}

export default function RecipesWorkspace({
  projectId,
  products = [],
  loadingProducts = false,
  canCreateItems = false,
  canUpdateItems = false,
  recipes = [],
  recipesLoading = false,
  onRefreshRecipes,
  onCreateSupply,
  onManageSupplies,
  initialProductId = "",
  onClearInitialProduct,
}) {
  const mobile = useMediaQuery("(max-width:599.95px)", { noSsr: true });
  const [search, setSearch] = React.useState("");
  const [filter, setFilter] = React.useState("all");
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  const [recipe, setRecipe] = React.useState(null);
  const [rows, setRows] = React.useState([]);
  const [loadingRecipe, setLoadingRecipe] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [feedback, setFeedback] = React.useState(null);
  const [dirty, setDirty] = React.useState(false);
  const [ingredientToAdd, setIngredientToAdd] = React.useState(null);
  const openedInitialRef = React.useRef("");

  const summaryByProduct = React.useMemo(
    () => new Map((recipes || []).map((item) => [String(item.productId), item])),
    [recipes],
  );

  const eligibleProducts = React.useMemo(() => {
    const query = search.trim().toLocaleLowerCase("es");
    return products
      .filter(sellableProduct)
      .filter((product) => {
        const hasRecipe = summaryByProduct.has(idOf(product));
        if (filter === "with" && !hasRecipe) return false;
        if (filter === "without" && hasRecipe) return false;
        if (!query) return true;
        return [nameOf(product), product.category || product.categoria, product.gastronomicMenu?.section]
          .filter(Boolean)
          .join(" ")
          .toLocaleLowerCase("es")
          .includes(query);
      })
      .sort((left, right) => {
        const leftMenu = left.gastronomicMenu?.enabled === true ? 0 : 1;
        const rightMenu = right.gastronomicMenu?.enabled === true ? 0 : 1;
        return leftMenu - rightMenu || nameOf(left).localeCompare(nameOf(right), "es");
      });
  }, [filter, products, search, summaryByProduct]);

  const ingredientOptions = React.useMemo(() => {
    const parentId = idOf(selectedProduct);
    return products
      .filter((product) => idOf(product) !== parentId)
      .filter((product) => active(product) && ["supply", "product"].includes(kindOf(product)) && stockTracked(product))
      .sort((left, right) => {
        const kindDiff = (kindOf(left) === "supply" ? 0 : 1) - (kindOf(right) === "supply" ? 0 : 1);
        return kindDiff || nameOf(left).localeCompare(nameOf(right), "es");
      });
  }, [products, selectedProduct]);

  const openEditor = React.useCallback(async (product) => {
    if (!product) return;
    setSelectedProduct(product);
    setRecipe(null);
    setRows([]);
    setFeedback(null);
    setDirty(false);
    setLoadingRecipe(true);
    try {
      const data = await recipesApi.get(projectId, idOf(product));
      const current = data?.recipe || null;
      setRecipe(current);
      setRows(ingredientDraft(current));
    } catch (error) {
      setFeedback({ severity: "error", message: error.message || "No se pudo cargar la receta." });
    } finally {
      setLoadingRecipe(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    const wanted = String(initialProductId || "");
    if (!wanted || openedInitialRef.current === wanted || loadingProducts) return;
    const product = products.find((item) => idOf(item) === wanted && sellableProduct(item));
    if (!product) return;
    openedInitialRef.current = wanted;
    openEditor(product);
  }, [initialProductId, loadingProducts, openEditor, products]);

  const closeEditor = () => {
    if (saving) return;
    if (dirty && !window.confirm("Hay cambios de receta sin guardar. ¿Cerrar igualmente?")) return;
    setSelectedProduct(null);
    setRecipe(null);
    setRows([]);
    setFeedback(null);
    setDirty(false);
    setIngredientToAdd(null);
    onClearInitialProduct?.();
  };

  const addIngredient = () => {
    if (!ingredientToAdd) return;
    const ingredientId = idOf(ingredientToAdd);
    if (rows.some((row) => row.productId === ingredientId)) {
      setFeedback({ severity: "warning", message: `${nameOf(ingredientToAdd)} ya está en la receta. Editá su cantidad en la línea existente.` });
      return;
    }
    if (!unitOf(ingredientToAdd)) {
      setFeedback({ severity: "warning", message: `${nameOf(ingredientToAdd)} necesita una unidad de stock antes de poder agregarse.` });
      return;
    }
    setRows((current) => [...current, {
      productId: ingredientId,
      quantityPerServing: "1",
      unitSnapshot: unitOf(ingredientToAdd),
      productNameSnapshot: nameOf(ingredientToAdd),
      product: ingredientToAdd,
      issue: null,
    }]);
    setIngredientToAdd(null);
    setFeedback(null);
    setDirty(true);
  };

  const save = async () => {
    if (!selectedProduct || saving) return;
    if (!rows.length) {
      setFeedback({ severity: "error", message: "Agregá al menos un ingrediente." });
      return;
    }
    const invalid = rows.find((row) => !Number.isFinite(Number(row.quantityPerServing)) || Number(row.quantityPerServing) <= 0);
    if (invalid) {
      setFeedback({ severity: "error", message: `La cantidad de ${invalid.product?.name || invalid.productNameSnapshot} debe ser mayor que cero.` });
      return;
    }
    try {
      setSaving(true);
      setFeedback(null);
      const data = await recipesApi.save(projectId, idOf(selectedProduct), rows.map((row) => ({
        productId: row.productId,
        quantityPerServing: Number(row.quantityPerServing),
      })));
      setRecipe(data?.recipe || null);
      setRows(ingredientDraft(data?.recipe));
      setDirty(false);
      setFeedback({ severity: "success", message: "Receta guardada. Las operaciones nuevas usarán esta versión." });
      await onRefreshRecipes?.();
    } catch (error) {
      setFeedback({ severity: "error", message: error.message || "No se pudo guardar la receta." });
    } finally {
      setSaving(false);
    }
  };

  const removeRecipe = async () => {
    if (!selectedProduct || !recipe || saving) return;
    if (!window.confirm(`¿Quitar la receta de ${nameOf(selectedProduct)}? El producto y el historial de stock se conservan.`)) return;
    try {
      setSaving(true);
      await recipesApi.remove(projectId, idOf(selectedProduct));
      setRecipe(null);
      setRows([]);
      setDirty(false);
      setFeedback({ severity: "success", message: "Receta quitada. El producto y sus movimientos históricos no se modificaron." });
      await onRefreshRecipes?.();
    } catch (error) {
      setFeedback({ severity: "error", message: error.message || "No se pudo quitar la receta." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2.2}>
      <Paper variant="outlined" sx={{ p: { xs: 1.6, sm: 2.2 }, borderRadius: 3.5, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} justifyContent="space-between">
          <Box>
            <Typography sx={{ fontWeight: 950, color: DARK, fontSize: 18 }}>Recetas por porción</Typography>
            <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.62)", mt: .4, maxWidth: 720 }}>
              Definí qué insumos consume cada plato. Al venderlo, WebProfit descuenta esas cantidades del stock y conserva la trazabilidad de la operación.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" startIcon={<Inventory2RoundedIcon />} onClick={onManageSupplies} sx={{ textTransform: "none", fontWeight: 900 }}>Gestionar insumos</Button>
            {canCreateItems ? <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={onCreateSupply} sx={{ bgcolor: ORANGE, textTransform: "none", fontWeight: 900 }}>Crear insumo</Button> : null}
          </Stack>
        </Stack>
      </Paper>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
        <TextField
          fullWidth
          size="small"
          label="Buscar plato o producto"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon fontSize="small" /></InputAdornment> }}
        />
        <TextField select size="small" label="Receta" value={filter} onChange={(event) => setFilter(event.target.value)} sx={{ minWidth: { sm: 190 } }}>
          <MenuItem value="all">Todos</MenuItem>
          <MenuItem value="with">Con receta</MenuItem>
          <MenuItem value="without">Sin receta</MenuItem>
        </TextField>
      </Stack>

      {loadingProducts || recipesLoading ? (
        <DashboardLoadingState label="Cargando productos y recetas…" minHeight={260} />
      ) : !eligibleProducts.length ? (
        <Alert severity="info">No hay productos vendibles que coincidan con estos filtros. Creá el plato desde Ítems y luego volvé a Recetas.</Alert>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }, gap: 1.4 }}>
          {eligibleProducts.map((product) => {
            const summary = summaryByProduct.get(idOf(product));
            const image = getProductImageUrls(product)[0];
            return (
              <Paper key={idOf(product)} variant="outlined" sx={{ p: 1.5, borderRadius: 3, borderColor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.10)" }}>
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Avatar variant="rounded" src={image || undefined} sx={{ width: 54, height: 54, borderRadius: 2, bgcolor: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.06)" }}><RestaurantMenuRoundedIcon /></Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontWeight: 950, color: DARK }}>{nameOf(product)}</Typography>
                    <Stack direction="row" spacing={.6} flexWrap="wrap" useFlexGap sx={{ mt: .55 }}>
                      {product.gastronomicMenu?.enabled === true ? <Chip size="small" label="En Carta" sx={{ height: 23, fontWeight: 850, color: ORANGE, bgcolor: "rgba(243,90,58,.08)" }} /> : null}
                      <Chip size="small" variant={summary ? "filled" : "outlined"} color={summary?.requiresReview ? "warning" : summary ? "success" : "default"} label={summary ? `Receta · ${summary.ingredientCount} ingrediente${summary.ingredientCount === 1 ? "" : "s"}` : "Sin receta"} sx={{ height: 23, fontWeight: 850 }} />
                    </Stack>
                  </Box>
                  {summary || canUpdateItems ? (
                    <Button size="small" variant="outlined" startIcon={canUpdateItems ? (summary ? <EditRoundedIcon /> : <AddRoundedIcon />) : null} onClick={() => openEditor(product)} sx={{ textTransform: "none", fontWeight: 900, whiteSpace: "nowrap" }}>
                      {canUpdateItems ? (summary ? "Editar" : "Crear") : "Ver"}
                    </Button>
                  ) : null}
                </Stack>
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog open={Boolean(selectedProduct)} onClose={closeEditor} fullWidth maxWidth="md" fullScreen={mobile}>
        <DialogTitle sx={{ pr: 6 }}>
          <Typography sx={{ fontWeight: 950, color: DARK }}>{recipe ? "Editar receta" : "Crear receta"}</Typography>
          <Typography variant="body2" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{selectedProduct ? `${nameOf(selectedProduct)} · cantidades por 1 porción/unidad vendida` : ""}</Typography>
          <IconButton aria-label="Cerrar editor de receta" onClick={closeEditor} disabled={saving} sx={{ position: "absolute", right: 12, top: 12 }}><CloseRoundedIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingRecipe ? <DashboardLoadingState label="Cargando receta…" minHeight={240} /> : (
            <Stack spacing={2}>
              {feedback ? <Alert severity={feedback.severity}>{feedback.message}</Alert> : null}
              {selectedProduct && stockTracked(selectedProduct) ? (
                <Alert severity="warning">Este producto terminado también controla stock. Crear una receta no cambia esa configuración automáticamente; revisá si querés controlar existencias del plato además de sus ingredientes.</Alert>
              ) : null}
              <Paper variant="outlined" sx={{ p: 1.4, borderRadius: 2.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 900, color: DARK, mb: 1 }}>Agregar ingrediente real del stock</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "flex-start" }}>
                  <Autocomplete
                    fullWidth
                    disabled={!canUpdateItems}
                    value={ingredientToAdd}
                    onChange={(_event, value) => setIngredientToAdd(value)}
                    options={ingredientOptions}
                    getOptionLabel={(option) => `${nameOf(option)} · ${kindOf(option) === "supply" ? "Insumo" : "Producto"}${unitOf(option) ? ` · ${unitOf(option)}` : " · sin unidad"}`}
                    isOptionEqualToValue={(option, value) => idOf(option) === idOf(value)}
                    getOptionDisabled={(option) => !unitOf(option)}
                    renderInput={(params) => <TextField {...params} size="small" label="Ingrediente / insumo" helperText="La unidad se toma del Ítem y no se escribe manualmente." />}
                  />
                  <Button variant="contained" onClick={addIngredient} disabled={!ingredientToAdd || !canUpdateItems} sx={{ minHeight: 40, bgcolor: DARK, textTransform: "none", fontWeight: 900 }}>Agregar</Button>
                </Stack>
                {canCreateItems ? <Button size="small" onClick={onCreateSupply} startIcon={<AddRoundedIcon />} sx={{ mt: 1, textTransform: "none", fontWeight: 850 }}>Crear un insumo que falta</Button> : null}
              </Paper>

              {!rows.length ? <Alert severity="info">La receta todavía está vacía. Agregá al menos un ingrediente para guardarla.</Alert> : (
                <Stack spacing={1}>
                  {rows.map((row, index) => {
                    const current = products.find((item) => idOf(item) === row.productId) || row.product;
                    const label = current ? nameOf(current) : row.productNameSnapshot;
                    const unit = current ? unitOf(current) : row.unitSnapshot;
                    const issue = row.issue || (!current
                      ? { message: "El ingrediente ya no existe. Quitalo o reemplazalo." }
                      : !active(current)
                        ? { message: "El ingrediente está inactivo." }
                        : !unit
                          ? { message: "El ingrediente no tiene unidad de stock." }
                          : row.unitSnapshot && unit !== row.unitSnapshot
                            ? { message: `La unidad cambió de ${row.unitSnapshot} a ${unit}. Revisá la cantidad por porción.` }
                            : null);
                    return (
                      <Paper key={`${row.productId}-${index}`} variant="outlined" sx={{ p: 1.35, borderRadius: 2.5, borderColor: issue ? "warning.light" : "rgba(var(--wp-panel-ink-rgb, 25,36,47),.12)" }}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2} alignItems={{ sm: "center" }}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 900, color: DARK }}>{label}</Typography>
                            <Typography variant="caption" sx={{ color: "rgba(var(--wp-panel-ink-rgb, 25,36,47),.68)" }}>{current && kindOf(current) === "supply" ? "Insumo" : "Producto físico"}{current ? ` · stock ${Number(current.stock || 0)}` : ""}</Typography>
                            {issue ? <Typography variant="caption" display="block" color="warning.main" sx={{ fontWeight: 800 }}>{issue.message}</Typography> : null}
                          </Box>
                          <TextField
                            size="small"
                            type="number"
                            disabled={!canUpdateItems}
                            label="Cantidad por porción"
                            value={row.quantityPerServing}
                            onChange={(event) => {
                              const value = event.target.value;
                              setRows((currentRows) => currentRows.map((item, rowIndex) => rowIndex === index ? { ...item, quantityPerServing: value } : item));
                              setDirty(true);
                            }}
                            inputProps={{ min: "0.000001", step: "any" }}
                            InputProps={{ endAdornment: <InputAdornment position="end">{unit || "sin unidad"}</InputAdornment> }}
                            error={row.quantityPerServing !== "" && Number(row.quantityPerServing) <= 0}
                            sx={{ width: { xs: "100%", sm: 230 } }}
                          />
                          {canUpdateItems ? <IconButton aria-label={`Quitar ${label}`} onClick={() => { setRows((currentRows) => currentRows.filter((_item, rowIndex) => rowIndex !== index)); setDirty(true); }} color="error"><DeleteRoundedIcon /></IconButton> : null}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}

              <Alert severity="info" icon={<RestaurantMenuRoundedIcon />}>
                {recipeSummaryText(nameOf(selectedProduct), rows)}
              </Alert>
              {recipe?.requiresReview ? <Alert severity="warning">La receta necesita revisión porque uno o más ingredientes cambiaron desde el último guardado.</Alert> : null}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
          <Box>{recipe && canUpdateItems ? <Button color="error" onClick={removeRecipe} disabled={saving} startIcon={<DeleteRoundedIcon />} sx={{ textTransform: "none", fontWeight: 850 }}>Quitar receta</Button> : null}</Box>
          <Stack direction="row" spacing={1}>
            <Button onClick={closeEditor} disabled={saving} sx={{ textTransform: "none", fontWeight: 850 }}>Cerrar</Button>
            {canUpdateItems ? <Button variant="contained" onClick={save} disabled={saving || loadingRecipe || rows.some((row) => row.issue)} startIcon={saving ? <WebProfitSpinner size={16} /> : undefined} sx={{ bgcolor: ORANGE, textTransform: "none", fontWeight: 900 }}>{saving ? "Guardando..." : "Guardar receta"}</Button> : null}
          </Stack>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
